import { z } from 'zod';
import { idSchema } from './common.js';
export const PARTNER_MAP_DEFAULT_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
export const PARTNER_MAP_EARTH_RADIUS_KM = 6371.0088;
export const PARTNER_MAP_MAX_MARKERS = 1500;
export const partnerLocationModeSchema = z.enum(['MAIN', 'SHIPPING', 'ALL']);
export const partnerLocationStatusSchema = z.enum([
    'PENDING',
    'PRECISE',
    'APPROXIMATE',
    'AMBIGUOUS',
    'NOT_FOUND',
    'ERROR',
    'MANUAL',
    'REVIEW',
]);
export const partnerMapFiltersShape = {
    roles: z.preprocess((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v), z
        .array(z.enum(['customer', 'prospect', 'supplier']))
        .max(3)
        .default([])),
    locationMode: partnerLocationModeSchema.default('MAIN'),
    city: z.string().trim().max(120).optional(),
    postalCode: z.string().trim().max(20).optional(),
    region: z.string().trim().max(120).optional(),
    contactDays: z.coerce.number().int().min(1).max(36500).optional(),
    neverContacted: z.enum(['true', 'false']).optional(),
    visitDays: z.coerce.number().int().min(1).max(36500).optional(),
    addressReview: z.enum(['true', 'false']).optional(),
    nearLat: z.coerce.number().min(-90).max(90).optional(),
    nearLng: z.coerce.number().min(-180).max(180).optional(),
    radiusKm: z.coerce.number().min(0.1).max(1000).optional(),
};
export const partnerMapViewportSchema = z
    .object({
    west: z.coerce.number().min(-180).max(180).default(-180),
    east: z.coerce.number().min(-180).max(180).default(180),
    south: z.coerce.number().min(-90).max(90).default(-85),
    north: z.coerce.number().min(-90).max(90).default(85),
    zoom: z.coerce.number().min(0).max(22).default(3),
})
    .refine((v) => v.south < v.north);
export const partnerCoordinatesSchema = z.object({
    latitude: z.number().finite().min(-90).max(90),
    longitude: z.number().finite().min(-180).max(180),
});
export const partnerPositionUpdateSchema = partnerCoordinatesSchema
    .extend({ version: z.number().int().min(1), reason: z.string().trim().min(3).max(500) })
    .strict();
export const partnerMapMarkerSchema = z.object({
    id: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    locations: z.number().int(),
    partners: z.number().int(),
    customer: z.boolean(),
    prospect: z.boolean(),
    supplier: z.boolean(),
    west: z.number(),
    east: z.number(),
    south: z.number(),
    north: z.number(),
});
export const partnerLocationSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    sourceKey: z.string(),
    kind: z.enum(['MAIN', 'SHIPPING', 'BILLING', 'OTHER']),
    label: z.string().nullable(),
    recipient: z.string().nullable(),
    address: z.string(),
    name: z.string(),
    number: z.string(),
    roles: z.array(z.enum(['customer', 'prospect', 'supplier'])),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    status: partnerLocationStatusSchema,
    accuracy: z.string().nullable(),
    source: z.string().nullable(),
    geocodingPublic: z.boolean().default(false),
    jobOrigin: z.string().default('LEGACY'),
    reviewReason: z.string().nullable().default(null),
    geocodedAt: z.string().nullable(),
    version: z.number().int(),
    distanceKm: z.number().nullable(),
});
export const partnerMapResponseSchema = z.object({
    data: z.array(partnerLocationSchema),
    markers: z.array(partnerMapMarkerSchema),
    meta: z.object({
        page: z.number().int(),
        pageSize: z.number().int(),
        total: z.number().int(),
        totalPages: z.number().int(),
        partners: z.number().int(),
        positioned: z.number().int(),
        missing: z.number().int(),
        viewport: z.number().int(),
    }),
    configuration: z.object({
        styleUrl: z.string().url(),
        geocoding: z.enum(['READY', 'NOT_CONFIGURED']),
        provider: z.literal('Nominatim'),
    }),
    generatedAt: z.string(),
});
export const partnerLocationDetailSchema = partnerLocationSchema.extend({
    tags: z.array(z.string()),
    industry: z.string().nullable(),
    priceGroup: z.string().nullable(),
    owner: z.string().nullable(),
    lastPersonalContactAt: z.string().nullable(),
    lastVisitAt: z.string().nullable(),
});
export function partnerDistanceKm(a, b) {
    const rad = Math.PI / 180;
    const h = Math.sin(((b.latitude - a.latitude) * rad) / 2) ** 2 +
        Math.cos(a.latitude * rad) *
            Math.cos(b.latitude * rad) *
            Math.sin(((b.longitude - a.longitude) * rad) / 2) ** 2;
    return 2 * PARTNER_MAP_EARTH_RADIUS_KM * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
}
export const partnerMapOwnersSchema = z.array(z.object({ id: idSchema, name: z.string() }));
export const partnerGeocodingRequestSchema = z
    .object({
    version: z.number().int().min(1),
    publicBusinessAddress: z.literal(true),
})
    .strict();
export const PARTNER_GEOCODING_MAX_ATTEMPTS = 4;
export const PARTNER_GEOCODING_INTERVAL_MS = 15000;
export const PARTNER_GEOCODING_QUEUE = 'partner-nominatim-v1';
//# sourceMappingURL=partner-map.js.map